import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { FiChevronDown, FiMenu, FiX } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expertiseDropdownOpen, setExpertiseDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const expertiseItems = [
    { name: 'Technology', href: '/expertise/technology', description: 'Software Development & IT Solutions' },
    { name: 'Healthcare', href: '/expertise/healthcare', description: 'Healthcare Technology & Digital Solutions' },
    { name: 'Financial Services', href: '/expertise/financial-services', description: 'FinTech & Banking Solutions' },
    { name: 'Life Sciences', href: '/expertise/life-sciences', description: 'Biotech & Pharmaceutical Technology' },
    { name: 'Enterprise Solutions', href: '/expertise/enterprise-solutions', description: 'Large-Scale Digital Transformation' },
    { name: 'E-commerce & Retail', href: '/expertise/e-commerce-retail', description: 'Next-Generation Shopping Experiences' }
  ];

  const navItems = [
    { name: 'Our Services', href: '/services', isHash: false },
    { name: 'Our Expertise', href: '/expertise', isHash: false, hasDropdown: true },
    { name: 'Careers', href: '#careers', isHash: true },
    { name: 'About Us', href: '/about', isHash: false },
    { name: 'Blog', href: '/blogs', isHash: false },
    { name: 'Contact', href: '/contact', isHash: false }
  ];

  const handleNavClick = (item) => {
    if (item.isHash && location.pathname !== '/') {
      // If we're not on the home page and clicking a hash link, go to home first
      window.location.href = '/' + item.href;
    }
    setIsOpen(false);
    setExpertiseDropdownOpen(false);
  };

  const handleExpertiseMouseEnter = () => {
    setExpertiseDropdownOpen(true);
  };

  const handleExpertiseMouseLeave = () => {
    setExpertiseDropdownOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-blue-100' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-2"
          >
            <Link to="/" className="flex items-center space-x-2">
              <img src="./Innofusion.png" alt="InnoFusion Logo" className="w-16 h-16 rounded-full" />
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent font-display tracking-tight">
                  InnoFusion
                </h1>
                <p className="text-s text-gray-600 -mt-1 font-body font-medium tracking-wide">Analytics</p>
              </div>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => {
              if (item.hasDropdown && item.name === 'Our Expertise') {
                return (
                  <div 
                    key={item.name}
                    className="relative"
                    onMouseEnter={handleExpertiseMouseEnter}
                    onMouseLeave={handleExpertiseMouseLeave}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 + 0.3 }}
                      whileHover={{ y: -2 }}
                      className="flex items-center"
                    >
                      <Link
                        to={item.href}
                        className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 relative group font-heading text-sm tracking-wide flex items-center space-x-1"
                      >
                        <span>{item.name}</span>
                        <FiChevronDown 
                          className={`transition-transform duration-200 ${
                            expertiseDropdownOpen ? 'rotate-180' : ''
                          }`} 
                          size={16} 
                        />
                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-700 transition-all duration-300 group-hover:w-full"></span>
                      </Link>
                    </motion.div>

                    {/* Dropdown Menu */}
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ 
                        opacity: expertiseDropdownOpen ? 1 : 0,
                        y: expertiseDropdownOpen ? 0 : -10,
                        visibility: expertiseDropdownOpen ? 'visible' : 'hidden'
                      }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-80 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-blue-100 overflow-hidden"
                      style={{ minWidth: '320px' }}
                    >
                      <div className="py-4">
                        <div className="px-6 py-2 border-b border-blue-100">
                          <h3 className="text-sm font-semibold text-gray-900 font-heading tracking-wide">Our Expertise Areas</h3>
                        </div>
                        
                        {expertiseItems.map((expertiseItem, idx) => (
                          <Link
                            key={expertiseItem.name}
                            to={expertiseItem.href}
                            onClick={() => setExpertiseDropdownOpen(false)}
                            className="block px-6 py-3 hover:bg-blue-50 transition-colors duration-200 group"
                          >
                            <div className="flex items-start space-x-3">
                              {/* <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mt-2 group-hover:scale-125 transition-transform duration-200"></div> */}
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200 font-heading tracking-wide">
                                  {expertiseItem.name}
                                </h4>
                                <p className="text-xs text-gray-600 mt-1 font-body">
                                  {expertiseItem.description}
                                </p>
                              </div>
                            </div>
                          </Link>
                        ))}
                        
                        <div className="px-6 py-3 border-t border-blue-100 mt-2">
                          <Link
                            to="/expertise"
                            onClick={() => setExpertiseDropdownOpen(false)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium font-accent tracking-wide flex items-center space-x-1 group"
                          >
                            <span>View All Expertise</span>
                            <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                );
              }

              return item.isHash ? (
                <motion.a
                  key={item.name}
                  href={item.href}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                  whileHover={{ y: -2 }}
                  onClick={() => handleNavClick(item)}
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 relative group font-heading text-sm tracking-wide"
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-700 transition-all duration-300 group-hover:w-full"></span>
                </motion.a>
              ) : (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                  whileHover={{ y: -2 }}
                >
                  <Link
                    to={item.href}
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 relative group font-heading text-sm tracking-wide"
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-700 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                </motion.div>
              );
            })}
            <Link to="/contact">
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-2 rounded-full font-medium shadow-lg hover:shadow-xl transition-all duration-300 font-accent tracking-wide"
            >
              Get Started
            </motion.button></Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200"
            >
              {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={false}
          animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden overflow-hidden bg-white/95 backdrop-blur-md rounded-lg mt-2 shadow-lg"
        >
          <div className="px-4 py-4 space-y-4">
            {navItems.map((item, index) => {
              if (item.hasDropdown && item.name === 'Our Expertise') {
                return (
                  <div key={item.name} className="space-y-2">
                    <motion.div
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className="block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 font-heading tracking-wide"
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                    
                    {/* Mobile Expertise Submenu */}
                    <div className="pl-4 space-y-2 border-l-2 border-blue-100">
                      {expertiseItems.map((expertiseItem, expertiseIdx) => (
                        <motion.div
                          key={expertiseItem.name}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: (index * 0.1) + (expertiseIdx * 0.05) + 0.1 }}
                        >
                          <Link
                            to={expertiseItem.href}
                            onClick={() => setIsOpen(false)}
                            className="block text-gray-600 hover:text-blue-600 text-sm transition-colors duration-200 font-body"
                          >
                            {expertiseItem.name}
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              }

              return item.isHash ? (
                <motion.a
                  key={item.name}
                  href={item.href}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleNavClick(item)}
                  className="block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 font-heading tracking-wide"
                >
                  {item.name}
                </motion.a>
              ) : (
                <motion.div
                  key={item.name}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className="block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 font-heading tracking-wide"
                  >
                    {item.name}
                  </Link>
                </motion.div>
              );
            })}
            <motion.button
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: navItems.length * 0.1 }}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-2 rounded-full font-medium shadow-lg font-accent tracking-wide"
            >
              Get Started
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Navbar; 